import { Button } from '@/components/forms/Button';
import { ThemeTypings, Select, Box } from '@chakra-ui/react'
import { Fragment, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Input, Text } from '@/index'

export type PaginationProps = {
  onChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  current?: number
  defaultCurrent?: number;
  total: number
  pageSizeOptions?: number[]
  defaultPageSize?: number
  showQuickJumper?: boolean
  showSizeChanger?: boolean
  colorScheme?: ThemeTypings["colorSchemes"];
  size?: "xs" | "sm" | "md",
  disabled?: boolean
};

export default function Pagination({
  onChange,
  onPageSizeChange,
  current = 0,
  defaultCurrent,
  total,
  pageSizeOptions,
  defaultPageSize,
  showQuickJumper,
  showSizeChanger,
  size = 'sm',
  disabled
}: PaginationProps) {
  const pageSizes = pageSizeOptions ?? [10, 20, 30, 40, 50];

  const [currentPage, setCurrentPage] = useState<number>(defaultCurrent ?? 0);
  const [currentPageSize, setCurrentPageSize] = useState<number>(defaultPageSize ?? pageSizes[0]);
  const pendingCurrentPage = useRef<number>(0);

  useEffect(() => {
    onPageSizeChange?.(currentPageSize)
  }, [currentPageSize])

  useEffect(() => {
    handleCurrentPageChange(current - 1)
  }, [current])

  // total page count
  const pageCount = useMemo(() => {
    const newPageCount = Math.floor(total / currentPageSize) || 1
    if (currentPage >= newPageCount) {
      handleCurrentPageChange(newPageCount - 1)
    }
    return newPageCount
  }, [currentPageSize]);

  const handleCurrentPageChange = useCallback((newPage: number) => {
    if (newPage < 0 || newPage >= pageCount) {
      return
    }
    setCurrentPage(newPage)
  }, [pageCount])



  // render single page num
  const renderPage = useCallback((index: number) => {
    const pageGap = 3;
    const lowerLimit = currentPage + pageGap > pageCount ? pageCount - pageGap * 2
      : currentPage - pageGap < 0
        ? 0
        : currentPage - pageGap;
    const upperLimit = currentPage - pageGap < 0
      ? pageGap * 2
      : currentPage + pageGap > pageCount
        ? pageCount - 1
        : currentPage + pageGap;

    const leftHide = lowerLimit > 1
    const rightHide = upperLimit < pageCount - 2

    if (!(index <= upperLimit && index >= lowerLimit)
      && ![0, pageCount - 1].includes(index)
      && pageCount > pageGap * 2
    ) {
      return null;
    }

    return <Fragment key={index}>
      {index === lowerLimit && leftHide &&
        <Text w="6" textAlign="center" userSelect={'none'}>
          ...
        </Text>}
      <Button
        size={size}
        fontSize={size}
        colorScheme={index === currentPage ? 'primary' : 'secondary'}
        onClick={() => handleCurrentPageChange(index)}
        disabled={disabled}
      >
        {index + 1}
      </Button>
      {index === upperLimit && rightHide &&
        <Text w="6" textAlign="center" userSelect={'none'} >
          ...
        </Text>}
    </Fragment>
  }, [currentPage, pageCount]);

  useEffect(() => {
    onChange?.(currentPage + 1)
  }, [currentPage])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const newPageNum = pendingCurrentPage.current - 1;
    if (e.key === 'Enter') {
      handleCurrentPageChange(newPageNum)
    }
  }, [handleCurrentPageChange])

  return (
    <Stack direction="row" mt="8" justify="flex-end" align="center" spacing="6">
      <Stack direction="row" spacing="1" alignItems={'center'}>
        {
          Array.from({ length: pageCount }).map((_, index) => renderPage(index))
        }
        {showSizeChanger && <Select disabled={disabled} w="fit-content" size={size} fontSize={size} onChange={e => setCurrentPageSize(Number(e.target.value))}>
          {
            pageSizes.map(size => <option key={size} value={size}>{size}</option>)
          }
        </Select>}
        {
          showQuickJumper && <>
            <Text fontSize={14}>Go To:</Text>
            <Input
              disabled={disabled}
              size={size}
              fontSize={size}
              type="number"
              w="12"
              onChange={e => pendingCurrentPage.current = Number(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </>
        }
      </Stack>
    </Stack>
  )
}